/**
 * OrderPurge — the audit document a purge writes, at
 * `workspaces/{orgId}/orderPurges/{orderId}`. It sits at the workspace root,
 * not under the event, because it must outlive the order it describes and
 * carry everything the cascade needs to resume once that order is gone —
 * `orderAccessToken` above all, which exists nowhere else after the delete.
 *
 * No client write path: the callable is the only writer. Read and list are
 * gated on `tickets.orders.purge` + mfa, the same authority that authorised
 * the destruction — the document names the buyer's Storage prefix and the
 * statutory numbers the purge left gaps in, so it is not a member-membership
 * read.
 *
 * The two states are a discriminated union on `status`, not a status plus a
 * nullable stamp: a `purged` record with no `completedAt` is unrepresentable,
 * and a resume path reading a `purging` record gets `null` from the type
 * rather than a maybe.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { PaymentModeSchema } from "./order.entity.ts";
import { OrderOriginSchema } from "./order-origin.const.ts";
import { SecretToken } from "./ticket.entity.ts";
import type { Actor } from "./workspace.entity.ts";

// The per-type capacity a purge handed back. Declared here, on the record
// that stores it, and imported by purgeOrder.action.ts's output — one
// declaration, so the callable's answer and the audit trail cannot drift.
export const QuotaReturnedSchema = z
	.record(z.string(), z.number().int().min(0))
	.describe(
		"ticket type id to the capacity this purge returned to that type — a per-type map, never a scalar, because each type carries its own counter and a partial refund may already have returned part of the order",
	);

// Fields justified:
//   orderId            — a snapshot of the destroyed order's id, held as a
//     field so the record still names its subject when it is read outside its
//     own path (a query result, a log line, an export) rather than only as the
//     leaf of a path the reader must still be holding.
//   eventId            — the order's own path is gone, so the parent cannot be
//     read back off it.
//   purgedBy           — who authorised an irreversible act.
//   startedAt          — when the order stopped existing.
//   origin / paymentEnv — the pair that names WHICH ground made this order
//     purgeable: a sandbox payment (`origin: "sale"`, `paymentEnv: "sandbox"`),
//     a member's issuance (`origin: "guest"`, `paymentEnv: null`, since a comp
//     is born carrying no payment at all), or a buyer's own free basket
//     (`origin: "sale"`, `paymentEnv: null` — the pair no priced order can
//     reach, since a priced sale is purgeable only on the sandbox ground). The
//     order they were read from no longer exists, so this record is the only
//     surviving evidence the guard was satisfied, and neither field states that
//     alone.
//   voucherCode        — the voucher whose use this purge returned; the
//     permission's own description promises the give-back, and the checkout
//     that named the code is not part of the record.
//   orderAccessToken / storagePrefix — the resume keys; see their describes.
//   invoiceNumber / correctionNumbers — the § 14 UStG gaps this purge explains.
//   quotaReturned      — what the counters actually moved by.
//   deletedTicketIds   — the cascade's own work list.
// Excluded:
//   orgId              — the document's own path carries it; a restated copy is
//     free to contradict the path it sits under.
//   buyerEmail / buyerName — a purge erases the order; retaining the buyer's
//     identity in a record that outlives it is the § 17 erasure the purge was
//     asked to perform, undone.
//   lines / total / refundedAmount — the money is not what a purge unwinds;
//     what it moved is quotaReturned and voucherCode, both recorded.
//   deletedStorageObjectCount — the caller is answered with it, but it counts
//     the objects the cascade removed under the prefix at one moment, not a
//     fact about the purged order; storagePrefix is the durable record.
//   a failure/error field — a cascade that dies leaves the record at
//     `purging`, which IS the failure state and the resume cursor both.
const OrderPurgeBase = z.object({
	orderId: z
		.string()
		.describe(
			"the purged order — a snapshot of its id, carried as a field so the record names its own subject wherever it is read, not only where the path is still in hand",
		),
	eventId: z
		.string()
		.describe(
			"the event the purged order belonged to — the order's own path is gone, so the parent cannot be read back off it",
		),
	purgedBy: z
		.string()
		.describe("the member uid that authorised the destruction"),
	startedAt: z
		.custom<Timestamp>()
		.describe(
			"when the deleting transaction committed — the moment the order stopped existing",
		),
	origin: OrderOriginSchema.default("sale").describe(
		"the purged order's own origin — with `paymentEnv`, the record of WHICH ground the callable's guard was satisfied on, since the document both were read off no longer exists. Defaults to `sale`: a record written while the guard admitted the sandbox ground alone describes a sale by construction, and a resume reads it back after this field exists",
	),
	paymentEnv: PaymentModeSchema.nullish().describe(
		"the purged order's `payment.env` — `sandbox` on the ground that admits a test order; null on an order born carrying no payment object at all, which is every guest issuance and every free buyer basket",
	),
	voucherCode: z
		.string()
		.nullish()
		.describe(
			"the voucher whose use this purge returned (`usedCount - 1`, clamped ≥0); null when the checkout carried none",
		),
	orderAccessToken: SecretToken.nullish().describe(
		"the purged order's capability token — the resume key, since orderAccess/{token} and the Storage prefix are reachable by nothing else once the order doc is gone; null when the order never reached fulfillment and minted none",
	),
	storagePrefix: z
		.string()
		.nullish()
		.describe(
			"`files/{token}/` — the Storage prefix the cascade deletes; null on an order that minted no access token and therefore no artifacts",
		),
	invoiceNumber: z
		.string()
		.nullish()
		.describe(
			"the § 14 UStG number the purged order held — never reclaimed, because the series is gapless-monotone by statute; recorded here as the explanation of the gap it leaves. Null on an order that never had one",
		),
	correctionNumbers: z
		.array(z.string())
		.describe(
			"the correction numbers the order's refunds drew from the same statutory series — the same gap-explaining job as invoiceNumber, one entry per correction; empty when the order was never refunded",
		),
	quotaReturned: QuotaReturnedSchema,
	deletedTicketIds: z
		.array(z.string())
		.describe(
			"every ticket the purge destroyed — the cascade's own work list for the walletTokens and walletRegistrations keyed by ticket id, which a re-invocation cannot re-derive from the deleted order's lines",
		),
});

export const OrderPurgeSchema = z.discriminatedUnion("status", [
	OrderPurgeBase.extend({
		status: z
			.literal("purging")
			.describe(
				"written inside the transaction that deletes the order; a document left here is the resume cursor a re-invocation reads",
			),
		completedAt: z
			.null()
			.describe(
				"no completion stamp exists while the cascade is unfinished — including after a crash mid-cascade",
			),
	}),
	OrderPurgeBase.extend({
		status: z
			.literal("purged")
			.describe(
				"stamped once every satellite and Storage object is gone — the cascade is finished",
			),
		// The presence check is the point of the split: a bare `z.custom` accepts
		// undefined, so a `purged` record with no stamp would still parse and the
		// union would enforce the invariant only at compile time.
		completedAt: z
			.custom<Timestamp>((value) => value != null)
			.describe("when the cascade finished"),
	}),
]);
export type OrderPurge = z.infer<typeof OrderPurgeSchema>;

export const OrderPurge = entity<Actor>()({
	schema: OrderPurgeSchema,
	actions: ["read", "list"] as const,
})
	.pipe(({ can, hasPermission, and }) => [
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.orders.purge"),
				me.mfa.eq(true),
			),
		),
		can("list").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.orders.purge"),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/orderPurges/{orderId}",
		}),
	);
